// MARK://dhk8/admin-shell/pages/modules/IngredientDetailPage
// Ficha "ver-primero" de un insumo (Product type=INGREDIENT) — Gustavo 2026-06-23.
// Header con foto + nombre + SKU + pills (estado: con/sin costo) + KPIs (costo,
// unidad, proveedores) + Tabs (General, Lotes, Movimientos, Proveedores, Historial).
// El tab General se ve primero; "Editar" abre el SectionedSheet con EL MISMO
// formulario que el modal de IngredientsPage (identidad/precio/SAT/media +
// presentaciones de compra + proveedores). Carga el insumo por id con
// catalog.getProduct; los lotes/movimientos se filtran por el(los) stockItemId
// de este producto (resueltos contra inventory.listInventory, porque un StockItem
// se identifica por productId+warehouseId y los lotes/movimientos referencian
// stockItemId, no productId). Proveedores via suppliers.listSupplierItems(productId).
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Plus, Trash2, Star } from 'lucide-react'

import * as cat from '../../lib/clients/catalog.js'
import * as inv from '../../lib/clients/inventory.js'
import * as sup from '../../lib/clients/suppliers.js'
import { listUnitConversions, resolveConversionFactor, normalizeUnit, type UnitConversion } from '../../lib/clients/units.js'
import type { Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import ImageUploader from '../../components/ImageUploader.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

type Tab = 'general' | 'lotes' | 'movimientos' | 'proveedores' | 'historial'

function mxn(cents: number): string {
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}
// Costo por unidad base: puede ser fracción de centavo ($0.0120/ml) → hasta 4 decimales.
function mxnFine(cents: number): string {
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}

function needsCost(p: Product): boolean {
  return (p.tags ?? []).includes('needs-attention:cost-0') || p.priceCents === 0
}

// Costo equivalente en las unidades de la MISMA dimensión, calculado desde el costo
// por unidad capturado — el usuario NO hace cuentas: ve directo $238/L = $0.2380/ml.
const COST_TARGETS = ['L', 'ML', 'KG', 'GR', 'PZA']
function costEquivalents(unit: string, costPesos: number, rows: UnitConversion[]): Array<{ unit: string; pesos: number }> {
  if (!unit || !(costPesos > 0)) return []
  const out: Array<{ unit: string; pesos: number }> = []
  const seen = new Set<string>()
  for (const t of [unit, ...COST_TARGETS]) {
    if (seen.has(t)) continue
    const f = t === unit ? 1 : resolveConversionFactor(unit, t, rows) // cuántas t por 1 unit
    if (f == null || !(f > 0)) continue
    seen.add(t)
    out.push({ unit: t, pesos: costPesos / f })
  }
  return out.length > 1 ? out.sort((a, b) => b.pesos - a.pesos) : [] // solo si hay conversión real
}

const MOVEMENT_LABEL: Record<string, string> = {
  RECEIPT: 'Recepción', CONSUMPTION: 'Consumo', TRANSFER: 'Transferencia',
  ADJUSTMENT: 'Ajuste', MERMA: 'Merma',
}
function movementTone(type: string): 'ok' | 'warn' | 'danger' | 'info' | 'neutral' {
  if (type === 'RECEIPT') return 'ok'
  if (type === 'MERMA') return 'danger'
  if (type === 'CONSUMPTION') return 'warn'
  if (type === 'TRANSFER') return 'info'
  return 'neutral'
}

interface InsumoForm {
  name: string
  unit: string
  costPesos: string
  description: string
  categoryId: string
  imageUrl: string | null
  imageKey: string | null
  satProductKey: string
  satUnitKey: string
}

export default function IngredientDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const product = useAsync((s) => cat.getProduct(id, s), [id])
  const cats = useAsync((s) => cat.listCategories(s))
  // StockItems del tenant → nos da el(los) stockItemId de ESTE producto para filtrar
  // lotes/movimientos (un StockItem se identifica por productId+warehouseId).
  const inventory = useAsync((s) => inv.listInventory(s), [id])
  const lots = useAsync((s) => inv.listLots(s), [id])
  const movements = useAsync((s) => inv.listMovements(s), [id])
  const supplierItems = useAsync((s) => (id ? sup.listSupplierItems(id, s) : Promise.resolve([])), [id])
  const suppliers = useAsync((s) => sup.listSuppliers(s))
  const packs = useAsync((s) => (id ? inv.listPackPresentations(id, s) : Promise.resolve([])), [id])
  const convs = useAsync((s) => listUnitConversions(s))

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<InsumoForm | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const p = product.data
  const catName = (cid: string | null | undefined) => (cid ? cats.data?.find((c) => c.id === cid)?.name ?? null : null)
  const supplierName = (sid: string) => suppliers.data?.find((s) => s.id === sid)?.legalName ?? sid
  const packName = (pid: string | null) => (pid ? packs.data?.find((x) => x.id === pid)?.name ?? null : null)

  // stockItemIds de este producto (puede tener stock en varios almacenes).
  const stockItemIds = useMemo(
    () => new Set((inventory.data ?? []).filter((it) => it.productId === id).map((it) => it.id)),
    [inventory.data, id],
  )
  const productLots = useMemo(() => (lots.data ?? []).filter((l) => stockItemIds.has(l.stockItemId)), [lots.data, stockItemIds])
  const productMovements = useMemo(() => (movements.data ?? []).filter((m) => stockItemIds.has(m.stockItemId)), [movements.data, stockItemIds])

  function openEdit() {
    if (!p) return
    setForm({
      name: p.name,
      unit: p.unit ?? 'GR',
      costPesos: (p.priceCents / 100).toFixed(2),
      description: p.description ?? '',
      categoryId: p.categoryId ?? '',
      imageUrl: p.imageUrl ?? null,
      imageKey: p.imageKey ?? null,
      satProductKey: p.satProductKey ?? '',
      satUnitKey: p.satUnitKey ?? '',
    })
    setErr(null)
    setEditing(true)
  }

  async function save() {
    if (!form || !p) return
    setErr(null)
    if (!form.name.trim()) { setErr('El nombre del insumo es obligatorio.'); return }
    const costCents = Math.max(0, Math.round((Number.parseFloat(form.costPesos) || 0) * 100))
    const name = form.name.trim()
    const unit = form.unit.trim() || 'GRM'
    const description = form.description.trim() || null
    const categoryId = form.categoryId || null
    const satProductKey = form.satProductKey.trim() || undefined
    const satUnitKey = form.satUnitKey.trim() || undefined
    setBusy(true)
    try {
      await cat.updateProduct({ ...p, name, unit, priceCents: costCents, description: description ?? undefined, categoryId: categoryId ?? '', imageUrl: form.imageUrl, satProductKey, satUnitKey })
      toast('Insumo actualizado', 'success')
      setEditing(false)
      product.reload()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo guardar el insumo.')
    } finally { setBusy(false) }
  }

  // Etiquetas de sistema (no editables); se filtra needs-attention.
  const editingTags = (p?.tags ?? []).filter((t) => !t.startsWith('needs-attention:'))

  const kpis = useMemo(() => {
    if (!p) return []
    const items: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'danger' | 'info' | 'neutral' }[] = [
      { label: 'Costo por unidad', value: mxn(p.priceCents), tone: needsCost(p) ? 'warn' : undefined },
      { label: 'Unidad', value: unitShort(p.unit) || '—' },
    ]
    if ((supplierItems.data ?? []).length > 0) items.push({ label: 'Proveedores', value: supplierItems.data!.length })
    return items
  }, [p, supplierItems.data])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/ingredients')} aria-label="Volver a insumos"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Insumo</h1><p className="adm-page__sub">Materias primas · ficha del insumo</p></div>
        </div>
      </div>

      {asyncState(product.loading, product.error, product.reload)}

      {p && (
        <EntityDetailPage
          title={p.name}
          subtitle={`SKU ${p.sku || '—'}${catName(p.categoryId) ? ` · ${catName(p.categoryId)}` : ''}`}
          image={p.imageUrl ?? null}
          pills={<>
            {needsCost(p)
              ? <StatusPill tone="warn" label="Sin costo" />
              : <StatusPill tone="ok" label="Con costo" />}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'lotes', label: 'Lotes', count: productLots.length },
              { id: 'movimientos', label: 'Movimientos', count: productMovements.length },
              { id: 'proveedores', label: 'Proveedores', count: supplierItems.data?.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del insumo"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre">{p.name}</Field>
              <Field label="SKU"><span className="adm-mono">{p.sku || '—'}</span></Field>
              <Field label="Categoría">{catName(p.categoryId) ?? <span className="adm-muted">Sin categoría</span>}</Field>
              <Field label="Descripción">{p.description || <span className="adm-muted">Sin descripción</span>}</Field>
              <Field label="Costo por unidad"><span className="adm-mono">{mxn(p.priceCents)}</span></Field>
              <Field label="Unidad">{unitShort(p.unit) || <span className="adm-muted">—</span>}</Field>
              <Field label="Clave SAT producto"><span className="adm-mono">{p.satProductKey || '—'}</span></Field>
              <Field label="Clave SAT unidad"><span className="adm-mono">{p.satUnitKey || '—'}</span></Field>
              {editingTags.length > 0 && <Field label="Etiquetas del sistema"><span className="adm-mono">{editingTags.join(' · ')}</span></Field>}
            </div>
          )}

          {tab === 'lotes' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(lots.loading || inventory.loading, lots.error ?? inventory.error, () => { lots.reload(); inventory.reload() })}
              <div className="adm-list">
                {!lots.loading && !inventory.loading && productLots.length === 0 && (
                  <div className="adm-empty">Sin lotes registrados para este insumo. Los lotes se crean al recibir órdenes de compra.</div>
                )}
                {productLots.map((l) => {
                  const remaining = l.qty - l.consumedQty
                  return (
                    <div key={l.id} className="adm-row">
                      <div className="adm-row__main">
                        <div className="adm-row__title"><span className="adm-mono">{l.lotNumber}</span></div>
                        <div className="adm-row__meta">
                          Caduca {fmtDate(l.expiryDate)}
                          {l.uom ? ` · ${unitShort(l.uom)}` : ''}
                          {` · consumido ${l.consumedQty}`}
                        </div>
                      </div>
                      <div className="adm-row__tail">
                        <StatusPill tone={remaining <= 0 ? 'danger' : 'ok'} label={remaining <= 0 ? 'Agotado' : 'Disponible'} />
                        <span className="adm-mono">{remaining}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {tab === 'movimientos' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(movements.loading || inventory.loading, movements.error ?? inventory.error, () => { movements.reload(); inventory.reload() })}
              <div className="adm-list">
                {!movements.loading && !inventory.loading && productMovements.length === 0 && (
                  <div className="adm-empty">Sin movimientos de inventario para este insumo.</div>
                )}
                {productMovements.map((m) => (
                  <div key={m.id} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title">{MOVEMENT_LABEL[m.type] ?? m.type}{m.reversed ? ' (reversado)' : ''}</div>
                      <div className="adm-row__meta">{fmtDateTime(m.createdAt)}{m.reason ? ` · ${m.reason}` : ''}{m.ref ? ` · ${m.ref}` : ''}</div>
                    </div>
                    <div className="adm-row__tail">
                      <StatusPill tone={movementTone(m.type)} label={MOVEMENT_LABEL[m.type] ?? m.type} />
                      <span className="adm-mono">{m.qty > 0 ? `+${m.qty}` : m.qty}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'proveedores' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(supplierItems.loading, supplierItems.error, supplierItems.reload)}
              <div className="adm-list">
                {!supplierItems.loading && (supplierItems.data ?? []).length === 0 && (
                  <div className="adm-empty">Sin proveedores. Edita el insumo para agregar de quién lo compras y a qué precio.</div>
                )}
                {(supplierItems.data ?? []).map((it) => (
                  <div key={it.id} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title">{supplierName(it.supplierId)}</div>
                      <div className="adm-row__meta">
                        {packName(it.presentationId) ? `${packName(it.presentationId)} · ` : 'Unidad base · '}
                        {it.currency}
                        {it.leadDays != null ? ` · ${it.leadDays} días de entrega` : ''}
                      </div>
                    </div>
                    <div className="adm-row__tail">
                      {it.isPreferred && <span className="adm-badge"><Star size={11} /> preferido</span>}
                      <span className="adm-mono">{mxn(it.unitCostCents)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={p.id} />}
        </EntityDetailPage>
      )}

      {p && form && (
        <SectionedSheet
          open={editing}
          title="Editar insumo"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}

          <FormSection title="Identidad">
            <label className="adm-field">
              <span className="adm-field__label">Nombre</span>
              <input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Café en grano, Leche entera, Azúcar…" />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Descripción</span>
              <textarea className="adm-textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notas del insumo: marca, presentación, proveedor habitual…" />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Categoría</span>
              <select className="adm-select" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                <option value="">Sin categoría</option>
                {(cats.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          </FormSection>

          <FormSection title="Precio y unidad">
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <UnitPicker label="Unidad" value={form.unit} onChange={(unit) => setForm({ ...form, unit })} />
              </div>
              <label className="adm-field" style={{ flex: 1 }}>
                <span className="adm-field__label">Costo por unidad (MXN)</span>
                <input className="adm-input" inputMode="decimal" min={0} step="0.01" value={form.costPesos} onChange={(e) => setForm({ ...form, costPesos: e.target.value })} placeholder="0.00" />
              </label>
            </div>
            {/* Costo equivalente — el sistema convierte, el usuario no hace cuentas.
                Ej. $238/L → $0.2380/ml. Si no hay conversión conocida, no se muestra. */}
            {(() => {
              const eq = costEquivalents(form.unit, Number.parseFloat(form.costPesos) || 0, convs.data ?? [])
              if (eq.length < 2) return null
              return (
                <div className="adm-banner adm-banner--info" role="status" style={{ display: 'grid', gap: 2, fontSize: 13 }}>
                  <span className="adm-field__label" style={{ margin: 0 }}>Costo equivalente</span>
                  {eq.map((e) => (
                    <span key={e.unit}><strong>{mxnFine(e.pesos * 100)}</strong> / {unitShort(e.unit)}</span>
                  ))}
                  <span className="adm-row__meta">Calculado de tu costo y las equivalencias de unidad. Para presentaciones de compra (cajas, garrafones) usa la sección de abajo.</span>
                </div>
              )
            })()}
          </FormSection>

          <FormSection title="Claves SAT (CFDI)">
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="adm-field" style={{ flex: 1 }}>
                <span className="adm-field__label">Clave de producto/servicio</span>
                <input className="adm-input" value={form.satProductKey} onChange={(e) => setForm({ ...form, satProductKey: e.target.value })} placeholder="ej. 50202306" />
              </label>
              <div style={{ flex: 1 }}>
                <UnitPicker mode="sat" label="Clave de unidad" value={form.satUnitKey} onChange={(satUnitKey) => setForm({ ...form, satUnitKey })} />
              </div>
            </div>
          </FormSection>

          <FormSection title="Media">
            <div className="adm-field">
              <span className="adm-field__label">Imagen del insumo</span>
              <ImageUploader value={form.imageUrl} onChange={(url) => setForm({ ...form, imageUrl: url, imageKey: url ? form.imageKey : null })} />
            </div>
          </FormSection>

          <FormSection title="Presentaciones de compra">
            <PackPresentationsEditor productId={p.id} baseUnit={form.unit} onSetCost={(pesos) => setForm({ ...form, costPesos: pesos })} />
          </FormSection>
          <FormSection title="Proveedores">
            <SupplierItemsEditor productId={p.id} baseUnit={form.unit} onChange={() => supplierItems.reload()} />
          </FormSection>
          <FormSection title="Avanzado">
            <div className="adm-field">
              <span className="adm-field__label">SKU</span>
              <div className="ing-readonly">{p.sku || '—'}</div>
            </div>
            <div className="adm-field">
              <span className="adm-field__label">Etiquetas del sistema</span>
              <div className="ing-readonly">{editingTags.length ? editingTags.join(' · ') : '—'}</div>
            </div>
          </FormSection>
          <style>{`.adm-spin { animation: ing-spin 1s linear infinite; } @keyframes ing-spin { to { transform: rotate(360deg); } }
            .ing-readonly { font-family: var(--font-mono, var(--font-sans)); font-size: 13px; color: var(--pos-fg-secondary); padding: 8px 10px; border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md, 8px); background: var(--pos-elevated); }`}</style>
        </SectionedSheet>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}

// ── editor de presentaciones de compra de un insumo ─────────────────────────
// Modelo de Gustavo: capturas la presentación TAL COMO LA COMPRAS — "Bote 5L" que
// CONTIENE 5 L — y opcionalmente a qué PRECIO la compraste; el sistema deriva la
// unidad base y el costo unitario (flujo inverso). La unidad base es la del insumo
// (no se vuelve a preguntar → se acabó el choque "Litro (l)" vs "Litro (L)").
function PackPresentationsEditor({ productId, baseUnit, onSetCost }: { productId: string; baseUnit: string; onSetCost?: (pesos: string) => void }) {
  const { toast } = useToast()
  const confirm = useConfirm()
  const packs = useAsync((s) => inv.listPackPresentations(productId, s), [productId])
  const convs = useAsync((s) => listUnitConversions(s))
  // contenido = cantidad + unidad de lo que trae la presentación (ej. 5 L).
  const [form, setForm] = useState({ name: '', qty: 1, contentUnit: baseUnit, pricePesos: '', useAsCost: true })
  const [manualFactor, setManualFactor] = useState('') // solo si no hay equivalencia conocida
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null) // id de la presentación en edición
  const [originalName, setOriginalName] = useState('')            // para detectar rename
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const convRows = convs.data ?? []
  // cuántas unidades BASE (del insumo) trae 1 unidad de contenido
  const perContent = form.contentUnit ? resolveConversionFactor(form.contentUnit, baseUnit, convRows) : null
  const autoFactor = perContent != null && form.qty > 0 ? Math.round(form.qty * perContent * 1e6) / 1e6 : null
  const manualNum = Number.parseFloat(manualFactor)
  const effectiveFactor = autoFactor ?? (manualFactor !== '' && manualNum > 0 ? manualNum : null)
  const needsManual = !!form.contentUnit && normalizeUnit(form.contentUnit) !== normalizeUnit(baseUnit) && perContent == null
  // flujo inverso: precio de compra de la presentación → costo por unidad base
  const priceCents = Math.round((Number.parseFloat(form.pricePesos) || 0) * 100)
  const costPerBaseCents = priceCents > 0 && effectiveFactor && effectiveFactor > 0 ? priceCents / effectiveFactor : null

  function openAdd() { setForm({ name: '', qty: 1, contentUnit: baseUnit, pricePesos: '', useAsCost: true }); setManualFactor(''); setEditingId(null); setOriginalName(''); setErr(null); setAdding(true) }
  function openEdit(p: { id: string; name: string; purchaseUnit: string; factor: number; baseUnit: string }) {
    // reconstruye cantidad+unidad desde el factor guardado; si la unidad no convierte,
    // cae a la unidad base con la cantidad = factor (preserva el valor).
    const conv = resolveConversionFactor(p.purchaseUnit, baseUnit, convRows)
    if (conv != null && conv > 0) {
      setForm({ name: p.name, qty: Math.round((p.factor / conv) * 1e6) / 1e6, contentUnit: p.purchaseUnit, pricePesos: '', useAsCost: false })
      setManualFactor('')
    } else {
      setForm({ name: p.name, qty: p.factor, contentUnit: baseUnit, pricePesos: '', useAsCost: false })
      setManualFactor('')
    }
    setEditingId(p.id); setOriginalName(p.name); setErr(null); setAdding(true)
  }
  async function save() {
    if (!form.name.trim() || !form.contentUnit.trim() || !(form.qty > 0)) { setErr('Completa nombre, cantidad (>0) y unidad del contenido.'); return }
    if (effectiveFactor == null || !(effectiveFactor > 0)) { setErr(`No conozco la equivalencia ${unitShort(form.contentUnit)}→${unitShort(baseUnit)}. Agrégala en Unidades, o escribe cuántas ${unitShort(baseUnit)} trae 1 ${form.name || 'presentación'}.`); return }
    setBusy(true); setErr(null)
    try {
      await inv.upsertPackPresentation({ productId, name: form.name.trim(), purchaseUnit: form.contentUnit.trim(), factor: effectiveFactor, baseUnit })
      // si se renombró en edición, la upsert (llave = nombre) creó una NUEVA; borra la vieja.
      if (editingId && originalName && originalName !== form.name.trim()) await inv.deletePackPresentation(editingId)
      // flujo inverso: si dio precio y eligió usarlo, fija el costo por unidad del insumo.
      if (form.useAsCost && costPerBaseCents != null) onSetCost?.((costPerBaseCents / 100).toFixed(4).replace(/\.?0+$/, ''))
      toast('Presentación guardada', 'success'); setAdding(false); setEditingId(null); packs.reload()
    }
    catch (e) { setErr(e instanceof Error ? e.message : 'No se pudo guardar la presentación.') }
    finally { setBusy(false) }
  }
  async function remove(id: string) {
    const ok = await confirm({ title: '¿Eliminar presentación?', tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await inv.deletePackPresentation(id); toast('Presentación eliminada', 'success') } catch (e) { toast(e instanceof Error ? e.message : 'Error', 'danger'); return }
    packs.reload()
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {asyncState(packs.loading, packs.error, packs.reload)}
      {(packs.data ?? []).length === 0 && !packs.loading && <p className="adm-row__meta">Sin presentaciones. Captura cómo la compras: ej. "Bote 5L" que trae 5 L.</p>}
      {(packs.data ?? []).map((p) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
          <span><strong>{p.name}</strong> · trae <span className="adm-mono">{p.factor}</span> {unitShort(p.baseUnit)}</span>
          <span style={{ display: 'flex', gap: 4 }}>
            <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openEdit(p)} aria-label="Editar presentación"><Pencil size={13} /></button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(p.id)} aria-label="Quitar presentación"><Trash2 size={13} /></button>
          </span>
        </div>
      ))}
      {!adding ? (
        <button type="button" className="adm-btn adm-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={openAdd}><Plus size={14} /> Agregar presentación</button>
      ) : (
        <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 8 }}>
          <span className="adm-field__label" style={{ margin: 0 }}>{editingId ? 'Editar presentación' : 'Nueva presentación'}</span>
          {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}
          <label className="adm-field"><span className="adm-field__label">¿Cómo la compras?</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bote 5L, Garrafón, Caja 24…" /></label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <label className="adm-field" style={{ flex: 1 }}><span className="adm-field__label">Contenido</span><input className="adm-input" type="number" min={0} step="any" value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} placeholder="5" /></label>
            <div style={{ flex: 1 }}><UnitPicker label="Unidad del contenido" value={form.contentUnit} onChange={(contentUnit) => setForm({ ...form, contentUnit })} /></div>
          </div>
          {/* conversión automática a la unidad base del insumo */}
          {autoFactor != null && (
            <div className="adm-banner adm-banner--ok" role="status" style={{ fontSize: 13 }}>
              1 {form.name || 'presentación'} = <strong>{autoFactor} {unitShort(baseUnit)}</strong> <span className="adm-row__meta">(unidad base del insumo, convertido automáticamente)</span>
            </div>
          )}
          {needsManual && (
            <div style={{ display: 'grid', gap: 6 }}>
              <p className="adm-row__meta">No conozco la equivalencia {unitShort(form.contentUnit)}→{unitShort(baseUnit)}. Agrégala en <strong>Unidades</strong>, o escribe cuántas {unitShort(baseUnit)} trae 1 {form.name || 'presentación'}:</p>
              <label className="adm-field"><span className="adm-field__label">{unitShort(baseUnit)} por presentación</span><input className="adm-input" type="number" min={0} step="any" value={manualFactor} onChange={(e) => setManualFactor(e.target.value)} placeholder="5000" /></label>
            </div>
          )}
          {/* flujo inverso: precio de compra → costo unitario */}
          <label className="adm-field">
            <span className="adm-field__label">Precio de compra (opcional)</span>
            <input className="adm-input" inputMode="decimal" min={0} step="0.01" value={form.pricePesos} onChange={(e) => setForm({ ...form, pricePesos: e.target.value })} placeholder="¿cuánto pagaste por esta presentación?" />
            {costPerBaseCents != null && (
              <span className="adm-row__meta" style={{ marginTop: 4 }}>= <strong>{mxnFine(costPerBaseCents)}</strong> por {unitShort(baseUnit)} <span style={{ opacity: 0.7 }}>(costo unitario, calculado)</span></span>
            )}
          </label>
          {costPerBaseCents != null && (
            <label className="adm-field adm-field--row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.useAsCost} onChange={(e) => setForm({ ...form, useAsCost: e.target.checked })} />
              <span className="adm-field__label" style={{ margin: 0 }}>Usar como costo por unidad del insumo ({mxnFine(costPerBaseCents)}/{unitShort(baseUnit)})</span>
            </label>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="adm-btn adm-btn--sm" onClick={() => { setAdding(false); setEditingId(null) }} disabled={busy}>Cancelar</button>
            <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={save} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── editor de proveedores del insumo (SupplierItem / catálogo de compra) ──────
// Qué proveedor surte el insumo, opcional sucursal + presentación, precio y si es
// el preferido. Refetch tras cada add/delete (y avisa al padre para refrescar el tab).
function SupplierItemsEditor({ productId, baseUnit, onChange }: { productId: string; baseUnit: string; onChange?: () => void }) {
  const { toast } = useToast()
  const confirm = useConfirm()
  const items = useAsync((s) => sup.listSupplierItems(productId, s), [productId])
  const suppliers = useAsync((s) => sup.listSuppliers(s))
  const packs = useAsync((s) => inv.listPackPresentations(productId, s), [productId])
  const emptyForm = () => ({ supplierId: '', supplierAddressId: '', presentationId: '', pricePesos: '', isPreferred: false })
  const [form, setForm] = useState(emptyForm())
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const supplierName = (id: string) => suppliers.data?.find((s) => s.id === id)?.legalName ?? id
  const packOf = (id: string | null) => (id ? packs.data?.find((p) => p.id === id) ?? null : null)
  const packName = (id: string | null) => packOf(id)?.name ?? null
  const selectedSupplier = suppliers.data?.find((s) => s.id === form.supplierId)

  // Costo POR UNIDAD BASE = precio de la presentación ÷ factor (cuántas base trae).
  // Sin presentación, el precio YA es por unidad base (factor 1). El sistema lo calcula
  // para que el usuario compare presentaciones/proveedores en la MISMA unidad y no haga
  // cuentas; un factor mal puesto salta como un costo/base fuera de rango.
  const costPerBase = (it: sup.SupplierItem) => {
    const pk = packOf(it.presentationId)
    const factor = pk && pk.factor > 0 ? pk.factor : 1
    return { cents: it.unitCostCents / factor, unit: pk?.baseUnit ?? baseUnit }
  }
  const sortedItems = useMemo(
    () => [...(items.data ?? [])].sort((a, b) => costPerBase(a).cents - costPerBase(b).cents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items.data, packs.data, baseUnit],
  )

  // preview en vivo del costo/unidad base mientras se captura el proveedor
  const formPack = packOf(form.presentationId || null)
  const formFactor = formPack && formPack.factor > 0 ? formPack.factor : 1
  const formBaseUnit = formPack?.baseUnit ?? baseUnit
  const formPriceCents = Math.round((Number.parseFloat(form.pricePesos) || 0) * 100)
  const formCpbCents = formPriceCents > 0 ? formPriceCents / formFactor : null

  function reloadAll() { items.reload(); onChange?.() }

  function openAdd() { setForm(emptyForm()); setErr(null); setAdding(true) }
  async function add() {
    if (!form.supplierId) { setErr('Selecciona un proveedor.'); return }
    const unitCostCents = Math.max(0, Math.round((Number.parseFloat(form.pricePesos) || 0) * 100))
    setBusy(true); setErr(null)
    try {
      await sup.upsertSupplierItem({
        productId,
        supplierId: form.supplierId,
        supplierAddressId: form.supplierAddressId || null,
        presentationId: form.presentationId || null,
        unitCostCents,
        currency: 'MXN',
        isPreferred: form.isPreferred,
      })
      toast('Proveedor guardado', 'success')
      setAdding(false); reloadAll()
    } catch (e) { setErr(e instanceof Error ? e.message : 'No se pudo guardar el proveedor.') }
    finally { setBusy(false) }
  }
  async function remove(id: string) {
    const ok = await confirm({ title: '¿Quitar proveedor del insumo?', tone: 'danger', confirmLabel: 'Quitar' })
    if (!ok) return
    try { await sup.deleteSupplierItem(id); toast('Proveedor quitado', 'success') } catch (e) { toast(e instanceof Error ? e.message : 'Error', 'danger'); return }
    reloadAll()
  }
  async function togglePreferred(it: sup.SupplierItem) {
    try { await sup.upsertSupplierItem({ id: it.id, productId: it.productId, supplierId: it.supplierId, supplierAddressId: it.supplierAddressId, presentationId: it.presentationId, unitCostCents: it.unitCostCents, currency: it.currency, leadDays: it.leadDays, isPreferred: !it.isPreferred }) }
    catch (e) { toast(e instanceof Error ? e.message : 'Error', 'danger'); return }
    reloadAll()
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {asyncState(items.loading, items.error, items.reload)}
      {(items.data ?? []).length === 0 && !items.loading && <p className="adm-row__meta">Sin proveedores. Agrega de quién compras este insumo y a qué precio.</p>}
      {sortedItems.map((it) => {
        const cpb = costPerBase(it)
        return (
        <div key={it.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
          <span>
            <strong>{supplierName(it.supplierId)}</strong>
            {packName(it.presentationId) ? ` · ${packName(it.presentationId)}` : ''}
            {' · '}<span className="adm-mono">{mxn(it.unitCostCents)}</span>
            {' → '}<span className="adm-mono" title="Costo por unidad base (calculado)">{mxnFine(cpb.cents)}/{unitShort(cpb.unit)}</span>
            {it.isPreferred && <span className="adm-badge" style={{ marginLeft: 6 }}><Star size={11} /> preferido</span>}
          </span>
          <span style={{ display: 'flex', gap: 4 }}>
            <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => togglePreferred(it)} aria-label={it.isPreferred ? 'Quitar preferido' : 'Marcar preferido'} title={it.isPreferred ? 'Quitar preferido' : 'Marcar preferido'}><Star size={13} /></button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(it.id)} aria-label="Quitar proveedor"><Trash2 size={13} /></button>
          </span>
        </div>
        )
      })}
      {!adding ? (
        <button type="button" className="adm-btn adm-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={openAdd}><Plus size={14} /> Agregar proveedor</button>
      ) : (
        <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 8 }}>
          {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}
          <label className="adm-field">
            <span className="adm-field__label">Proveedor</span>
            <select className="adm-select" value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value, supplierAddressId: '' })}>
              <option value="">— Selecciona proveedor —</option>
              {(suppliers.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.legalName}</option>)}
            </select>
          </label>
          {selectedSupplier && selectedSupplier.addresses.length > 0 && (
            <label className="adm-field">
              <span className="adm-field__label">Sucursal (opcional)</span>
              <select className="adm-select" value={form.supplierAddressId} onChange={(e) => setForm({ ...form, supplierAddressId: e.target.value })}>
                <option value="">— Sin sucursal —</option>
                {selectedSupplier.addresses.map((a) => <option key={a.id} value={a.id}>{a.line1}{a.city ? ` · ${a.city}` : ''}</option>)}
              </select>
            </label>
          )}
          <label className="adm-field">
            <span className="adm-field__label">Presentación (opcional)</span>
            <select className="adm-select" value={form.presentationId} onChange={(e) => setForm({ ...form, presentationId: e.target.value })}>
              <option value="">— Unidad base —</option>
              {(packs.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.factor} {unitShort(p.baseUnit)})</option>)}
            </select>
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Precio (MXN){formPack ? ` por ${formPack.name}` : ' (por unidad base)'}</span>
            <input className="adm-input" inputMode="decimal" min={0} step="0.01" value={form.pricePesos} onChange={(e) => setForm({ ...form, pricePesos: e.target.value })} placeholder="0.00" />
            {formCpbCents != null && (
              <span className="adm-row__meta" style={{ marginTop: 4 }}>= <strong>{mxnFine(formCpbCents)}</strong> por {unitShort(formBaseUnit)} <span style={{ opacity: 0.7 }}>(costo por unidad base, calculado)</span></span>
            )}
          </label>
          <label className="adm-field adm-field--row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.isPreferred} onChange={(e) => setForm({ ...form, isPreferred: e.target.checked })} />
            <span className="adm-field__label" style={{ margin: 0 }}>Proveedor preferido</span>
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="adm-btn adm-btn--sm" onClick={() => setAdding(false)} disabled={busy}>Cancelar</button>
            <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={add} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      )}
    </div>
  )
}
